/**
 * Safe accessor for the parent site's LogRocket instance.
 * LogRocket is initialized by the host page (e.g. ChooseTexasPower),
 * so it lives on `window.LogRocket`. These helpers gracefully no-op
 * when the instance isn't available (dev, non-production, etc.).
 */

declare global {
  interface Window {
    LogRocket?: {
      track: (eventName: string, properties?: Record<string, unknown>) => void
      log: (...args: unknown[]) => void
      identify: (userId: string, traits?: Record<string, unknown>) => void
      sessionURL: () => string
    }
  }
}

const MONARCH_EXPERIENCE_EVENT = 'monarch_experience_resolved' as const
const PAGE_LOAD_EVENT = 'page_load' as const

export interface MonarchExperiencePayload {
  readonly experience: {
    readonly experienceName: string
    readonly experienceNumber: string
  }
  readonly audienceName: string
  readonly audienceId: string
  readonly slotId: string
  readonly url: string
  readonly hasRouterParam: boolean
  readonly routerParamValue: string | null
}

export interface PageLoadPayload {
  readonly referrer: string
  readonly url: string
}

function getLogRocket(): Window['LogRocket'] | undefined {
  if (typeof window === 'undefined') return undefined
  return window.LogRocket
}

export function logRocketLog(message: string, data?: unknown): void {
  const logRocket = getLogRocket()
  if (!logRocket) return
  try {
    logRocket.log(message, data)
  } catch (error) {
    console.error('Failed to log to LogRocket:', error)
  }
}

export function trackMonarchExperience(payload: MonarchExperiencePayload): void {
  const logRocket = getLogRocket()
  if (!logRocket) return

  try {
    // LogRocket.track only indexes flat key-value properties — nested objects
    // serialize as [object Object]. Flatten experience into top-level fields.
    const { experience, ...rest } = payload
    logRocket.track(MONARCH_EXPERIENCE_EVENT, { ...rest, ...experience })
    logRocket.log(`Monarch resolved: ${payload.experience.experienceName}`)
  } catch (error) {
    console.error('Failed to track Monarch experience:', error)
  }
}

export function trackStrataLoad(payload: PageLoadPayload): void {
  const logRocket = getLogRocket()
  if (!logRocket) return

  try {
    logRocket.track(PAGE_LOAD_EVENT, payload as unknown as Record<string, unknown>)
    logRocket.log(`Page load: referrer=${payload.referrer}, url=${payload.url}`)
  } catch (error) {
    console.error('Failed to track Strata load:', error)
  }
}

/**
 * Safely extracts experience details from the Monarch traffic flow result.
 * `monarchResult.source.trafficFlow` is a union of MonarchEvaluationsRule |
 * MonarchEvaluationsTrafficFlow — only the latter has `returnDetails`.
 * This narrows the type instead of suppressing errors with @ts-expect-error.
 */
export function getMonarchExperienceDetails(monarchResult: unknown): {
  experienceName: string
  experienceNumber: string
  audienceName: string
  audienceId: string
} {
  const source = (monarchResult as Record<string, unknown>)?.source as
    Record<string, unknown> | undefined
  const trafficFlow = source?.trafficFlow as Record<string, unknown> | undefined

  if (trafficFlow && 'returnDetails' in trafficFlow && trafficFlow.returnDetails) {
    const returnDetails = trafficFlow.returnDetails as Record<string, unknown>
    return {
      experienceName:
        typeof returnDetails.experienceName === 'string' ? returnDetails.experienceName : '',
      experienceNumber:
        typeof returnDetails.experienceNumber === 'number'
          ? String(returnDetails.experienceNumber)
          : '',
      audienceName: typeof trafficFlow.audienceName === 'string' ? trafficFlow.audienceName : '',
      audienceId: typeof trafficFlow.audienceId === 'string' ? trafficFlow.audienceId : '',
    }
  }

  return { experienceName: '', experienceNumber: '', audienceName: '', audienceId: '' }
}

export function buildMonarchExperiencePayload({
  experience,
  audienceName,
  audienceId,
  slotId,
}: {
  readonly experience: {
    readonly experienceName: string
    readonly experienceNumber: string
  }
  readonly audienceName: string
  readonly audienceId: string
  readonly slotId: string
}): MonarchExperiencePayload {
  // Capture the router query param to help diagnose cases where users
  // land with router=tg but Monarch resolves to the wrong experience.
  const searchParams = new URLSearchParams(window.location.search)
  const routerParamValue = searchParams.get('router')

  return {
    experience,
    audienceName,
    audienceId,
    slotId,
    url: window.location.href,
    hasRouterParam: routerParamValue !== null,
    routerParamValue,
  }
}
